
import { supabase } from './client';

type StorageSetupResult = 
  | { success: true }
  | { error: string; localOnly: boolean };

type AvatarUploadResult = 
  | { url: string; isLocal?: boolean }
  | { error: string; isLocal?: boolean };

export const setupAvatarStorage = async (): Promise<StorageSetupResult> => {
  try {
    // Check if bucket exists
    const { data: buckets, error: listError } = await supabase.storage.listBuckets();
    
    if (listError) {
      console.error('Error listing buckets:', listError);
      throw new Error(`Error listing buckets: ${listError.message}`);
    }
    
    const avatarBucketExists = buckets?.some(bucket => bucket.name === 'avatars');
    
    // If the bucket doesn't exist, we'll just return early instead of trying to create it
    // since we're hitting RLS permissions issues with bucket creation
    if (!avatarBucketExists) {
      console.log('Avatars bucket does not exist. Using local storage only.');
      return { 
        error: 'Avatars bucket not available',
        localOnly: true
      };
    }

    return { success: true };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Error setting up storage';
    console.error('Error setting up storage:', error);
    return { 
      error: errorMessage,
      localOnly: true
    };
  }
};

// Helper function to handle avatar upload
export const uploadAvatar = async (file: File, userId: string): Promise<AvatarUploadResult> => {
  try {
    if (!file) return { error: 'No file provided' };
    
    // Generate a unique file name to avoid collisions
    const fileExt = file.name.split('.').pop();
    const fileName = `${userId}-${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = `${userId}/${fileName}`;
    
    // First check if storage is available
    const storageStatus = await setupAvatarStorage();
    
    // If we can't use the bucket, store in local storage as base64
    if ('localOnly' in storageStatus && storageStatus.localOnly) {
      console.log('Using local storage for avatar');
      return handleLocalAvatarStorage(file, userId);
    }
    
    // Upload the file to Supabase
    const { error: uploadError, data: uploadData } = await supabase.storage
      .from('avatars')
      .upload(filePath, file, { upsert: true });
    
    if (uploadError) {
      console.error('Error uploading avatar:', uploadError);
      // Fall back to local storage on error
      return handleLocalAvatarStorage(file, userId);
    }
    
    // Get the public URL
    const { data } = supabase.storage.from('avatars').getPublicUrl(filePath);
    
    if (!data || !data.publicUrl) {
      console.error('Failed to get public URL for uploaded avatar');
      return handleLocalAvatarStorage(file, userId);
    }
    
    console.log('Avatar uploaded successfully to Supabase:', data.publicUrl);
    return { url: data.publicUrl };
  } catch (error) {
    console.error('Exception uploading avatar:', error);
    // Fall back to local storage on any error
    return handleLocalAvatarStorage(file, userId);
  }
};

// Helper function to store avatar in localStorage as base64
const handleLocalAvatarStorage = async (file: File, userId: string): Promise<AvatarUploadResult> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      try {
        // Store the image as data URL in localStorage
        localStorage.setItem(`avatar-${userId}`, dataUrl);
        console.log('Avatar stored in local storage');
        resolve({ url: dataUrl, isLocal: true });
      } catch (err) {
        console.error('Error storing avatar in localStorage:', err);
        resolve({ error: 'Failed to store avatar', isLocal: false });
      }
    };
    reader.onerror = () => {
      resolve({ error: 'Failed to read file' });
    };
    reader.readAsDataURL(file);
  });
};
